import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, Send, FileCheck, Clock, X, PlayCircle, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useUserNames } from "@/lib/useUserNames";

const STATUS = {
  requested: "border-amber-200 bg-amber-50 text-amber-700",
  granted: "border-emerald-200 bg-emerald-50 text-emerald-700",
  denied: "border-rose-200 bg-rose-50 text-rose-700",
  expired: "border-gray-200 bg-gray-50 text-gray-700",
};

function Kpi({ label, value, icon: Icon, testid }) {
  return (
    <div className="kpi-card" data-testid={testid}>
      <div className="mb-2 flex items-center justify-between"><div className="text-xs font-semibold uppercase tracking-wider text-ink-muted">{label}</div><Icon size={16} className="text-brand" /></div>
      <div className="font-heading text-3xl font-semibold">{value}</div>
    </div>
  );
}

export default function AbdmCenter() {
  const [stats, setStats] = useState(null);
  const [consents, setConsents] = useState([]);
  const [logs, setLogs] = useState([]);
  const [patientQuery, setPatientQuery] = useState("");
  const [patients, setPatients] = useState([]);
  const [chosen, setChosen] = useState(null);
  const [purpose, setPurpose] = useState("Care Management");
  const [expiry, setExpiry] = useState(30);
  const nameOf = useUserNames();

  const load = async () => {
    const [s, c, l] = await Promise.all([
      api.get("/abdm/dashboard"), api.get("/abdm/consents"), api.get("/abdm/logs"),
    ]);
    setStats(s.data); setConsents(c.data.items); setLogs(l.data.items);
  };
  useEffect(() => { load(); }, []);

  const search = async (q) => {
    setPatientQuery(q);
    if (q.length < 2) { setPatients([]); return; }
    const { data } = await api.get("/patients", { params: { q, limit: 8 } });
    setPatients(data.items);
  };

  const requestConsent = async () => {
    if (!chosen) { toast.error("Select a patient"); return; }
    try {
      const { data } = await api.post("/abdm/consent/request", { patient_id: chosen.id, purpose, expiry_days: Number(expiry) });
      const gs = data.gateway_status || "skipped";
      if (data.mode === "live" && gs === "success") {
        toast.success(`Consent sent live to ABDM (${data.consent_id})`);
      } else if (data.mode === "live") {
        toast.warning(`Consent saved locally but ABDM gateway returned: ${gs}`);
      } else {
        toast.info(`Consent request created (stub) · ${data.consent_id}`);
      }
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const pushCareContext = async () => {
    if (!chosen) { toast.error("Select a patient"); return; }
    try {
      const { data } = await api.post("/abdm/care-context/push", {
        patient_id: chosen.id,
        care_contexts: [{ referenceNumber: `CC-${Date.now()}`, display: `${purpose} — ${new Date().toLocaleDateString()}` }],
      });
      const gs = data.gateway_status || "skipped";
      if (data.mode === "live" && gs === "success") toast.success(`Care context pushed live (${data.count})`);
      else if (data.mode === "live") toast.warning(`ABDM gateway rejected push: ${gs}`);
      else toast.info("Care context queued (stub — ABDM disabled for this hospital)");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const sendNotify = async () => {
    try {
      const { data } = await api.post("/abdm/notify", { notification_type: "DATA_TRANSFER_STATUS", payload: { status: "TRANSFERRED", at: new Date().toISOString() } });
      const gs = data.gateway_status || "skipped";
      if (data.mode === "live" && gs === "success") toast.success("Notify sent live");
      else if (data.mode === "live") toast.warning(`Notify rejected: ${gs}`);
      else toast.info("Notify queued (stub)");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const [e2eRunning, setE2eRunning] = useState(false);
  const [e2eResult, setE2eResult] = useState(null);
  const runE2eCheck = async () => {
    if (!chosen) { toast.error("Pick an ABHA-linked patient first (Consent tab)."); return; }
    setE2eRunning(true);
    setE2eResult(null);
    try {
      const { data } = await api.post("/abdm/e2e-check", { patient_id: chosen.id });
      setE2eResult(data);
      if (data.ok) toast.success(`ABDM E2E check OK · ${data.environment}`);
      else toast.warning(`ABDM E2E check finished with failures — see the report below.`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setE2eRunning(false); }
  };

  const updateConsent = async (cid, status) => {
    await api.patch(`/abdm/consents/${cid}/status`, { status });
    load();
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <div className="section-label">Ayushman Bharat Digital Mission</div>
        <h1 className="font-heading text-3xl font-semibold">ABDM Center · M1 – M4</h1>
        <p className="mt-1 text-sm text-ink-muted">ABHA linking, consent workflow, doc push (M2) and gateway notify (M4). Live mode activates once your hospital's ABDM keys are saved in Settings → ABDM.</p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Kpi label="ABHA linked" value={stats.abha_linked} icon={ShieldCheck} testid="abdm-kpi-linked" />
          <Kpi label="ABHA today" value={stats.abha_today} icon={ShieldCheck} testid="abdm-kpi-today" />
          <Kpi label="Consents granted" value={stats.consent_granted} icon={FileCheck} testid="abdm-kpi-granted" />
          <Kpi label="Pending consents" value={stats.consent_pending} icon={Clock} testid="abdm-kpi-pending" />
        </div>
      )}

      <Tabs defaultValue="consent">
        <TabsList>
          <TabsTrigger value="consent" data-testid="abdm-tab-consent">Consent (M3)</TabsTrigger>
          <TabsTrigger value="m2m4" data-testid="abdm-tab-m2m4">Doc push (M2) · Notify (M4)</TabsTrigger>
          <TabsTrigger value="consents" data-testid="abdm-tab-list">Consent log</TabsTrigger>
          <TabsTrigger value="logs" data-testid="abdm-tab-txn">Transaction log</TabsTrigger>
        </TabsList>

        <TabsContent value="consent" className="mt-4">
          <Card className="p-5">
            <div className="section-label mb-3">Request consent (M3)</div>
            <div className="form-grid">
              <div className="md:col-span-2">
                <Label>Patient</Label>
                {chosen ? (
                  <div className="mt-1 flex items-center justify-between rounded-md border border-border p-2">
                    <div><div className="font-medium">{chosen.name}</div><div className="text-xs text-ink-muted font-mono">{chosen.uhid}</div></div>
                    <Button variant="outline" size="sm" onClick={() => setChosen(null)}>Change</Button>
                  </div>
                ) : (
                  <>
                    <Input value={patientQuery} onChange={e => search(e.target.value)} placeholder="Search patient…" data-testid="abdm-patient-search" />
                    {patients.length > 0 && (
                      <div className="mt-1 max-h-48 overflow-auto rounded-md border border-border">
                        {patients.map(p => (
                          <button key={p.id} onClick={() => { setChosen(p); setPatients([]); setPatientQuery(""); }}
                            className="block w-full border-b border-border px-3 py-2 text-left text-sm hover:bg-secondary">
                            <div className="font-medium">{p.name}</div><div className="text-xs text-ink-muted">{p.uhid} · {p.mobile}</div>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
              <div>
                <Label>Purpose of use</Label>
                <Select value={purpose} onValueChange={setPurpose}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Care Management","Break the Glass","Public Health","Healthcare Payment","Disease Specific Healthcare Research","Self Requested"].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Expiry (days)</Label><Input type="number" value={expiry} onChange={e => setExpiry(e.target.value)} /></div>
            </div>
            <div className="mt-4 flex justify-end">
              <Button onClick={requestConsent} className="bg-leaf hover:bg-leaf-600" data-testid="abdm-consent-request"><Send size={14} className="mr-2" />Send consent request</Button>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="m2m4" className="mt-4">
          <Card className="p-5 space-y-4">
            <div>
              <div className="section-label">Push care contexts (M2)</div>
              <p className="mt-1 text-xs text-ink-muted">Send the selected patient's new care contexts (visits, lab orders, surgeries) to ABDM so they become discoverable in the Consent Manager. Requires the patient to already have an ABHA linked.</p>
              <div className="mt-3 flex items-center gap-3">
                <div className="text-sm">{chosen ? <>Selected: <b>{chosen.name}</b> · {chosen.uhid}</> : <span className="text-ink-muted">Pick a patient on the Consent tab first.</span>}</div>
                <Button onClick={pushCareContext} disabled={!chosen} className="bg-brand hover:bg-brand-700" data-testid="abdm-m2-push">
                  <Send size={14} className="mr-2" />Push care context
                </Button>
              </div>
            </div>
            <div className="border-t border-border pt-4">
              <div className="section-label">Gateway notify (M4)</div>
              <p className="mt-1 text-xs text-ink-muted">Send a generic notification through the ABDM gateway (data-transfer status, callbacks etc.).</p>
              <div className="mt-3 flex items-center gap-3">
                <Button onClick={sendNotify} variant="outline" data-testid="abdm-m4-notify">
                  <Send size={14} className="mr-2" />Send notify
                </Button>
              </div>
            </div>

            <div className="border-t border-border pt-4">
              <div className="section-label">End-to-end check (M1 → M2 → M3 → M4)</div>
              <p className="mt-1 text-xs text-ink-muted">Runs all four milestones in sequence against the live gateway using the selected patient. Requires ABDM enabled + an ABHA-linked patient. Every run is persisted in the transaction log as an <code>e2e_check</code> event.</p>
              <div className="mt-3 flex items-center gap-3">
                <Button onClick={runE2eCheck} disabled={!chosen || e2eRunning} className="bg-leaf hover:bg-leaf-600" data-testid="abdm-e2e-run">
                  {e2eRunning ? <Loader2 size={14} className="mr-2 animate-spin" /> : <PlayCircle size={14} className="mr-2" />}
                  Run ABDM E2E check
                </Button>
                {e2eResult && (
                  <Badge variant="outline" className={e2eResult.ok ? "border-leaf/40 bg-leaf-50 text-leaf-700" : "border-rose-200 bg-rose-50 text-rose-700"}>
                    {e2eResult.ok ? "All steps OK" : "Partial / failed"}
                  </Badge>
                )}
              </div>

              {e2eResult && (
                <div className="mt-4 space-y-2" data-testid="abdm-e2e-report">
                  <div className="text-xs text-ink-muted">Environment: <b>{e2eResult.environment}</b> · Patient UHID: <b className="font-mono">{e2eResult.patient_uhid}</b> · Ran at {(e2eResult.ran_at || "").slice(0, 19).replace("T", " ")}</div>
                  {e2eResult.steps.map((s) => {
                    const ok = s.status === "success";
                    return (
                      <div key={s.step} className="flex items-start gap-3 rounded-md border border-border p-3">
                        <div className="pt-0.5">
                          {ok ? <CheckCircle2 size={18} className="text-leaf" /> : <XCircle size={18} className="text-rose-500" />}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <div className="font-medium">{s.step}</div>
                            <div className="text-xs text-ink-muted">{s.took_ms} ms</div>
                          </div>
                          <div className="mt-1 text-xs">
                            <span className="text-ink-muted">status:</span> <span className={ok ? "text-leaf-700" : "text-rose-600"}>{s.status}</span>
                            <span className="ml-3 text-ink-muted">gateway:</span> <span className="font-mono">{s.gateway_status}</span>
                          </div>
                          {(s.response?.error || s.response?.txn_id || s.response?.consent_id || s.response?.notificationId) && (
                            <div className="mt-1 truncate text-[11px] font-mono text-ink-muted" title={JSON.stringify(s.response)}>
                              {(() => {
                                const err = s.response?.error || "";
                                if (err.trim().startsWith("<!DOCTYPE") || err.trim().startsWith("<HTML")) {
                                  const m = err.match(/<TITLE>([^<]+)<\/TITLE>/i);
                                  return `HTML error response${m ? ` — ${m[1]}` : ""}`;
                                }
                                return (err || s.response.txn_id || s.response.consent_id || s.response.notificationId).slice(0, 200);
                              })()}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="consents" className="mt-4">
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary text-xs uppercase tracking-wider text-ink-muted">
                  <tr><th className="px-4 py-2 text-left">Consent ID</th><th className="px-4 py-2 text-left">Patient</th><th className="px-4 py-2 text-left">Purpose</th><th className="px-4 py-2 text-left">Status</th><th className="px-4 py-2 text-left">Created</th><th></th></tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {consents.length === 0 && <tr><td colSpan="6" className="px-4 py-10 text-center text-ink-muted">No consents yet.</td></tr>}
                  {consents.map(c => (
                    <tr key={c.id} className="row-enter">
                      <td className="px-4 py-2 font-mono text-xs">{c.id}</td>
                      <td className="px-4 py-2"><div className="font-medium">{c.patient_name}</div><div className="text-xs font-mono text-ink-muted">{c.patient_uhid}</div></td>
                      <td className="px-4 py-2">{c.purpose}</td>
                      <td className="px-4 py-2"><Badge variant="outline" className={STATUS[c.status] || ""}>{c.status}</Badge></td>
                      <td className="px-4 py-2 text-xs text-ink-muted">{(c.created_at||"").slice(0,16).replace("T"," ")}</td>
                      <td className="px-4 py-2 text-right">
                        {c.status === "requested" && (
                          <>
                            <Button size="sm" variant="outline" className="mr-2 border-leaf text-leaf" onClick={() => updateConsent(c.id, "granted")}>Grant</Button>
                            <Button size="sm" variant="outline" className="border-rose-300 text-rose-600" onClick={() => updateConsent(c.id, "denied")}><X size={12} className="mr-1" />Deny</Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="logs" className="mt-4">
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary text-xs uppercase tracking-wider text-ink-muted">
                  <tr><th className="px-4 py-2 text-left">When</th><th className="px-4 py-2 text-left">Event</th><th className="px-4 py-2 text-left">Status</th><th className="px-4 py-2 text-left">By</th><th className="px-4 py-2 text-left">Response</th></tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {logs.length === 0 && <tr><td colSpan="5" className="px-4 py-10 text-center text-ink-muted">No transactions yet.</td></tr>}
                  {logs.slice(0, 100).map(l => {
                    const gw = (l.response || {}).gateway;
                    const mode = (l.response || {}).mode;
                    const statusOk = l.status === "success";
                    return (
                      <tr key={l.id}>
                        <td className="px-4 py-2 text-xs text-ink-muted">{(l.created_at||"").slice(0,19).replace("T"," ")}</td>
                        <td className="px-4 py-2 capitalize">{l.event_type.replace(/_/g," ")}</td>
                        <td className="px-4 py-2">
                          <Badge variant="outline" className={statusOk ? "border-leaf/40 bg-leaf-50 text-leaf-700" : "border-rose-200 bg-rose-50 text-rose-700"} data-testid="abdm-log-status">{l.status}</Badge>
                          {mode && <Badge variant="outline" className="ml-2 border-sky-200 bg-sky-50 text-sky-700">{mode}</Badge>}
                        </td>
                        <td className="px-4 py-2 text-xs">{nameOf(l.user_email, l.user_email)}</td>
                        <td className="px-4 py-2 text-[11px] font-mono text-ink-muted" title={JSON.stringify(gw || l.response)}>{(gw && gw.error) ? String(gw.error).slice(0, 60) : JSON.stringify(l.response).slice(0, 60)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
